import deleteCategoryApi from '@/api/delete/delete-category';
import getCategoriesApi from '@/api/get/get-categories';
import getCategoryApi from '@/api/get/get-category';
import updateCategoryApi from '@/api/patch/user/update-category';
import createCategoryApi from '@/api/post/create-category';
import Alert from '@/components/alert/alert-component';
import { PrimaryButton } from '@/components/button/button-component';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import { IconResolver } from '@/components/icon-resolver/icon-resolver-component';
import CreateEditModal from '@/components/modal/modal-component';
import PageWrapper from '@/components/page-wrapper/page-wrapper-component';
import PermissionWrapper from '@/components/permission-wrapper/permission-wrapper-component';
import StatusIndicator from '@/components/status-indicator/status-indicator-component';
import SubRow from '@/components/table/components/sub-row/sub-row-component';
import ViewEditDelete from '@/components/table/components/view-edit-delete/view-edit-delete';
import Table from '@/components/table/table';
import { useLazyQuery, useMutation } from '@/hooks/use-api';
import { Heading2Text } from '@/utils/global-fonts';
import uploadToTempS3File from '@/utils/upload-to-s3-file';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { FiEdit } from 'react-icons/fi';
import { useLocation } from 'react-router-dom';
import Styled from './categories-all-style';
import CategoryAddEditForm from './category-add-edit-form';

/**
 * Page that shows all Categories in a table format.
 * @returns {JSX.Element} The rendered Category Management page component.
 */
const CategoriesAll = () => {
  const location = useLocation();
  const [modalAction, setModalAction] = useState({ label: '', id: null });
  const [s3Loading, setS3Loading] = useState(false);
  const [fetchingPresets, setFetchingPresets] = useState(true);
  const [defaultVariables, setDefaultVariables] = useState({});
  const [defaultSort, setDefaultSort] = useState('created_at:DESC');
  const tableId = 'category-list';

  const {
    data: allCategoriesData,
    count,
    error: allCategoriesError,
    loading: allCategoriesIsLoading,
    refetch: getAllCategories,
  } = useLazyQuery(getCategoriesApi);

  const refetch = useCallback(
    (query) => {
      const qStr = `${query ?? ''}${query ? '&' : ''}filter=parent_category$eq=null`;
      return getAllCategories(qStr);
    },
    [getAllCategories],
  );

  // API calls
  const [createCategory, { loading: createCategoryLoading }] =
    useMutation(createCategoryApi);
  const [updateCategory, { loading: updateCategoryLoading }] =
    useMutation(updateCategoryApi);
  const [deleteCategory, { loading: deleteCategoryLoading }] =
    useMutation(deleteCategoryApi);

  const getTablePresets = useCallback(() => {
    const tablePreferences = localStorage.getItem('tablePreferences');
    if (tablePreferences) {
      const tableConfigs = JSON.parse(tablePreferences)[tableId];

      if (tableConfigs) {
        const { query } = tableConfigs;

        if (query?.length > 0) {
          // Get cached search value and apply it
          const searchQuery = query?.filter((q) => q.includes('search='));
          if (searchQuery?.length > 0) {
            const tempSearch = searchQuery[0].replace('search=', '');
            setDefaultVariables({ ...defaultVariables, search: tempSearch });
          }

          // Get cached sort value and apply it
          const sortQuery = query?.filter((q) => q.includes('sort='));
          if (sortQuery?.length > 0) {
            const tempSort = sortQuery[0].replace('sort=', '');
            setDefaultSort(tempSort);
          }

          // Get cached page size and apply it
          const pageSizeQuery = query?.filter((q) => q.includes('limit='));
          if (pageSizeQuery?.length > 0) {
            const tempPageSize = parseInt(
              pageSizeQuery?.[0]?.replace('limit=', ''),
              10,
            );
            setDefaultVariables({ ...defaultVariables, limit: tempPageSize });
          }
        }
      }
    }
    setFetchingPresets(false);

    // This is disabled because we don't want to re-run this function when the defaultVariables changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // USE EFFECTS
  useEffect(() => {
    getTablePresets();
  }, [getTablePresets]);

  const launchDeleteConfirmation = useCallback(
    (categoryData) => {
      ConfirmationModal({
        title: 'Delete Category',
        text: `Are you sure you want to delete ${categoryData.values.name}?`,
        onConfirm: () => {
          deleteCategory(categoryData?.original?.id).then(() => {
            refetch().then(() => {
              Alert({
                icon: 'success',
                text: 'Category deleted successfully',
              });
            });
          });
        },
      });
    },
    [deleteCategory, refetch],
  );

  const renderStatusPill = useCallback(({ row }) => {
    const { status } = row.original;
    return (
      <Styled.PillContainer>
        <StatusIndicator status={status} />
      </Styled.PillContainer>
    );
  }, []);

  const renderMultiSelect = useCallback(({ row }) => {
    const isMultiSelect = row.original.is_multi_select;
    return isMultiSelect ? <Styled.MultiSelectIcon /> : null;
  }, []);

  const handleEdit = useCallback(
    (row) => {
      const categoryDetails = row.original;
      setModalAction({ label: 'Edit', id: categoryDetails.id });
    },
    [setModalAction],
  );

  const handleDelete = useCallback(
    (row) => {
      launchDeleteConfirmation(row);
    },
    [launchDeleteConfirmation],
  );

  const Actions = useCallback(
    ({ row }) => (
      <ViewEditDelete
        feature="category"
        onEdit={() => handleEdit(row)}
        onDelete={() => handleDelete(row)}
        onView={null}
        onDuplicate={null}
      />
    ),
    [handleDelete, handleEdit],
  );

  const renderSelectedIcon = useCallback(({ row }) => {
    if (row?.original?.icon?.includes('http')) {
      return (
        <Styled.IconContainer>
          <Styled.IconImage src={row?.original?.icon} />
        </Styled.IconContainer>
      );
    }
    return (
      <Styled.IconContainer>
        {IconResolver(row.original.icon)}
      </Styled.IconContainer>
    );
  }, []);

  const renderRowExpander = useCallback(({ row }) => {
    const hasChildren = !!row?.original?.has_children;

    return hasChildren ? (
      <span {...row.getToggleRowExpandedProps()}>
        {row.isExpanded ? <Styled.OpenGroupIcon /> : <Styled.ClosedGroupIcon />}
      </span>
    ) : null;
  }, []);

  const renderRowName = useCallback((level) => {
    const renderLevel = (id) => {
      const levels = Array(level).fill('');
      return levels
        .map((l, index) => index)
        .map((l) => <Styled.SubCategoryIcon key={id + l} />);
    };
    const renderCell = (row) => (
      <>
        {renderLevel(row?.original?.id)}
        <Styled.EnterIcon />
        <span>{row.cell.value}</span>
      </>
    );
    return renderCell;
  }, []);

  const closeCreateEditModal = () => {
    setModalAction({ label: '', id: null });
  };

  const handleMutation = (newFormData) => {
    if (modalAction.label === 'Edit') {
      updateCategory(modalAction.id, newFormData)
        .then(() => {
          setS3Loading(false);
          refetch().then(() => {
            closeCreateEditModal();
            Alert({
              icon: 'success',
              text: 'Category updated successfully',
            });
          });
        })
        .catch((e) => {
          setS3Loading(false);
          Alert({
            icon: 'error',
            text: 'There was a problem updating your category',
            message: e.message,
          });
        });
    } else {
      createCategory(newFormData)
        .then(() => {
          setS3Loading(false);
          closeCreateEditModal();
          Alert({
            icon: 'success',
            text: 'Product Category created successfully',
          });
          refetch();
        })
        .catch(() => {
          setS3Loading(false);
          Alert({
            icon: 'error',
            text: 'There was a problem creating your Product Category',
          });
        });
    }
  };

  const onSubmitForm = (formData) => {
    setS3Loading(true);
    /**
     * We get is_multi_select as a string from the form.
     * We need to convert it to a boolean to send to the API is_multi_select true/false.
     */
    const newFormData = {
      ...formData,
      is_multi_select: formData.is_multi_select === 'is_multi_select',
    };

    // if update icon is null, set it to default
    if (newFormData.icon === null) {
      newFormData.icon = 'default';
    }

    if (newFormData.icon && typeof newFormData.icon === 'object') {
      uploadToTempS3File(newFormData?.icon).then((data) => {
        const updatedFormData = {
          ...newFormData,
          icon: data.url,
        };
        handleMutation(updatedFormData);
      });
    } else {
      handleMutation(newFormData);
    }
  };

  const loading =
    allCategoriesIsLoading ||
    deleteCategoryLoading ||
    createCategoryLoading ||
    updateCategoryLoading ||
    s3Loading;

  const TableHeaders = useCallback(
    (level) => [
      {
        Header: '',
        id: 'expander',
        accessor: 'expander',
        disableSortBy: true,
        search: true,
        Cell: renderRowExpander,
      },
      {
        Header: 'ICON',
        id: 'icon',
        accessor: 'icon',
        width: 60,
        search: true,
        Cell: renderSelectedIcon,
      },
      {
        Header: 'NAME',
        id: 'name',
        accessor: 'name',
        search: true,
        Cell: renderRowName(level),
      },
      {
        Header: 'DESCRIPTION',
        id: 'description',
        accessor: 'description',
        search: true,
      },
      {
        Header: 'MULTIPLE',
        id: 'is_multi_select',
        accessor: 'is_multi_select',
        search: true,
        Cell: renderMultiSelect,
      },
      {
        Header: 'PRODUCTS',
        id: 'products',
        accessor: 'products',
        search: true,
      },
      {
        Header: 'STATUS',
        id: 'status',
        accessor: 'status',
        search: true,
        Cell: renderStatusPill,
      },
      {
        Header: 'ACTIONS',
        id: 'actions',
        Cell: Actions,
      },
    ],
    [
      renderRowExpander,
      renderRowName,
      renderMultiSelect,
      Actions,
      renderStatusPill,
      renderSelectedIcon,
    ],
  );

  useEffect(
    () =>
      location?.state?.dashboard && setModalAction({ label: 'Add', id: null }),
    [location?.state?.dashboard],
  );

  return (
    <PageWrapper>
      <Styled.MainPageContainer>
        <Styled.Heading>
          <Heading2Text>Global Categories</Heading2Text>
          <PermissionWrapper permissions={['category.create']}>
            <PrimaryButton
              title="New Category"
              onClick={() => setModalAction({ label: 'Add', id: null })}
              buttonHeight="40px"
              icon={<FiEdit />}
            />
          </PermissionWrapper>
        </Styled.Heading>
        <Table
          feature="category"
          onRowEdit={(row) => handleEdit(row)}
          onRowDelete={(row) => handleDelete(row)}
          subRowSameHeaders
          columns={TableHeaders(0)}
          data={allCategoriesData ?? []}
          tableOptions={['search', 'columns']}
          id={tableId}
          defaultSort={defaultSort}
          defaultVariables={defaultVariables}
          dataCount={count ?? 0}
          downloadAccessor="category-list"
          error={allCategoriesError}
          loading={loading || fetchingPresets}
          refetch={refetch}
          getDownloadData={getCategoriesApi}
          renderRowSubComponent={({ row }) => (
            <RenderRowSubComponent
              tableHeaders={TableHeaders}
              row={row}
              key={row.id}
              level={1}
            />
          )}
        />
      </Styled.MainPageContainer>
      <CreateEditModal
        open={!!modalAction.label}
        close={closeCreateEditModal}
        width="30%"
        alignItems="flex-start"
        title={`${modalAction.label} Category`}
        titleFontSize="28px"
        bodyStyle={{ width: '100%', padding: 0 }}
        loading={loading}
      >
        <FormWrapper
          onSubmit={onSubmitForm}
          defaultValues={async () => {
            const categoryId = modalAction.id;
            if (!categoryId) return {};
            const category = await getCategoryApi(categoryId);
            const { data: categoryData } = category;

            /**
             * We get is_multi_select true or false from the API.
             * Need to convert it to a string to be used in the form as a radio button.
             */
            const refactoredFormData = {
              ...categoryData,
              is_multi_select: categoryData.is_multi_select
                ? 'is_multi_select'
                : 'is_single_select',
            };

            return refactoredFormData;
          }}
        >
          <CategoryAddEditForm
            onSubmit={onSubmitForm}
            setModalAction={setModalAction}
            modalAction={modalAction}
            loading={loading}
          />
        </FormWrapper>
      </CreateEditModal>
    </PageWrapper>
  );
};

const RenderRowSubComponent = ({ row: subRow, tableHeaders, level }) => {
  const parentCategoryId = subRow?.original?.id;

  const {
    data: subCategoriesData,
    error: subCategoriesError,
    loading: subCategoriesIsLoading,
    refetch: refetchSubCategories,
  } = useLazyQuery(getCategoriesApi);

  useEffect(() => {
    refetchSubCategories(`filter=parent_category$eq=${parentCategoryId}`);
  }, [refetchSubCategories, parentCategoryId]);

  return (
    <SubRow
      loading={subCategoriesIsLoading}
      error={subCategoriesError}
      columns={tableHeaders(level)}
      data={subCategoriesData ?? []}
      cy="tblAccountCurrentContractsSubTable"
      id="category-list"
      checkMe="renderRowSubComponent"
      maxHeight={200}
      renderRowSubComponent={({ row }) => (
        <RenderRowSubComponent
          tableHeaders={tableHeaders}
          row={row}
          key={row.id}
          level={level + 1}
        />
      )}
    />
  );
};

RenderRowSubComponent.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  tableHeaders: PropTypes.array,
  level: PropTypes.number,
};

RenderRowSubComponent.defaultProps = {
  tableHeaders: [],
  level: 0,
};

export default CategoriesAll;
